import React, { useEffect, useState, useMemo } from 'react';
import {
  PlusCircle,
  Edit3,
  Trash2,
  Copy,
  Link as LinkIcon,
  Settings,
  ChevronDown,
  CheckCircle,
  AlertCircle,
  User,
  Mail,
  Phone,
  NotebookText
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { baseTeacher, SignupFormField } from '../components/interfaces/All';
import { useSupabase } from '../context/SupabaseContext';

// Intents:
// - see the list of forms (web signup form)
// - signup form ID is stored in teacher's settings (handleSetTeacherSettings)
// - students signing up will be connected to the teacher's account
// - teachers can navigate to this page to copy the link and paste it on instagram
// - future: teachers can select fields for the form (name, last name, phone, email are default)

interface SignupForm {
  id: string;
  name: string;
  fields: SignupFormField[];
  created_at: string;
  student_count: number;
}

const FormsList: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const [teacherSettings, setTeacherSettings] = useState(baseTeacher.teacher_settings);
  const [forms, setForms] = useState<SignupForm[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [newFormName, setNewFormName] = useState('');
  const [selectedForm, setSelectedForm] = useState<SignupForm | null>(null);
  const [showFieldEditor, setShowFieldEditor] = useState(false);
  const [formFields, setFormFields] = useState<SignupFormField[]>([
    { id: 'first_name', label: 'First Name', type: 'text', required: true, default: true },
    { id: 'last_name', label: 'Last Name', type: 'text', required: true, default: true },
    { id: 'phone', label: 'Phone Number', type: 'tel', required: true, default: true },
    { id: 'email', label: 'Email Address', type: 'email', required: true, default: true },
    { id: 'note', label: 'Note', type: 'textarea', required: false, default: true }
  ]);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  const { state, updateTeacherData } = useSupabase();

  // Fetch forms from teacher settings
  useMemo(() => {
    if (state.userRole !== 'teacher' || !state.userProfile.id) return;
    
    // Load forms from teacher settings (aplicable only for teachers)
    const storedForms = state.userProfile.teacher_settings?.signup_forms || [];
    setForms(storedForms);
    setTeacherSettings(state.userProfile.teacher_settings || baseTeacher.teacher_settings);
    
    // Load form students if form ID is in URL
    if (id) {
      const form = storedForms.find((f: SignupForm) => f.id === id);
      if (form) {
        setSelectedForm(form);
        // fetchSignupFormStudents(id);
      }
    }
  }, [state.userProfile, id]);

  const handleSetTeacherSettings = (settings: any) => {
    if (state.userRole !== 'teacher' || !state.userProfile.id) return;
    
    setTeacherSettings(settings);
    const timeout = setTimeout(async () => {
      await updateTeacherData({
        id: state.userProfile.id!,
        teacher_settings: settings
      });
    }, 800); // Debounce 800ms
    
    return () => clearTimeout(timeout);
  };

  const createNewForm = async () => {
    if (!newFormName.trim()) return;
    
    setIsCreating(true);
    
    const newForm: SignupForm = {
      id: `form_${Date.now()}`,
      name: newFormName,
      fields: formFields.filter(f => f.required || f.default),
      created_at: new Date().toISOString(),
      student_count: 0
    };
    
    // Update teacher settings with new form
    const updatedSettings = {
      ...teacherSettings,
      signup_forms: [...forms, newForm]
    };
    
    setForms([...forms, newForm]);
    handleSetTeacherSettings(updatedSettings);
    
    setNewFormName('');
    setIsCreating(false);
    setSelectedForm(newForm);
  };

  const deleteForm = async (formId: string) => {
    const updatedForms = forms.filter(f => f.id !== formId);
    setForms(updatedForms);
    
    const updatedSettings = {
      ...teacherSettings,
      signup_forms: updatedForms
    };
    
    handleSetTeacherSettings(updatedSettings);
    setSelectedForm(null);
  };

  const copyFormLink = async (formId: string) => {
    const formLink = `${window.location.origin}/signup-form/${formId}`;
    
    try {
      await navigator.clipboard.writeText(formLink);
      setCopySuccess(formId);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const updateFormFields = (formId: string, newFields: SignupFormField[]) => {
    const updatedForms = forms.map(form => 
      form.id === formId 
        ? { ...form, fields: newFields.filter(f => f.required || f.default) }
        : form
    );
    
    setForms(updatedForms);
    
    const updatedSettings = {
      ...teacherSettings,
      signup_forms: updatedForms
    };
    
    handleSetTeacherSettings(updatedSettings);
    
    // Update selected form
    if (selectedForm?.id === formId) {
      setSelectedForm(updatedForms.find(f => f.id === formId) || null);
    }
  };

  const toggleFieldRequirement = (fieldId: string) => {
    const updatedFields = formFields.map(field =>
      field.id === fieldId
        ? { ...field, required: !field.required }
        : field
    );
    setFormFields(updatedFields);
  };

  return (
    <div className="fade-in max-w-4xl mx-auto pt-4">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <LinkIcon className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Signup Forms</h1>
            <p className="text-text-light text-sm">Create and manage public signup forms for your students</p>
          </div>
        </div>
        
        {/* Create New Form */}
        <div className="card mb-6">
          <div className="flex items-center gap-3 p-6 pb-4 border-b border-border">
            <PlusCircle className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold">Create New Form</h2>
          </div>
          <div className="p-6">
            <div className="flex gap-4">
              <input
                type="text"
                value={newFormName}
                onChange={(e) => setNewFormName(e.target.value)}
                placeholder="Enter form name (e.g., 'Summer Lessons 2026')"
                className="w-full px-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800"
              />
              <button
                onClick={createNewForm}
                disabled={!newFormName.trim() || isCreating}
                className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Forms List */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Forms List Sidebar */}
        <div className="lg:col-span-1">
          <div className="card">
            <div className="flex items-center gap-3 p-6 pb-4 border-b border-border">
              <Settings className="w-6 h-6 text-primary" />
              <h2 className="text-xl font-bold">Your Forms</h2>
            </div>
            <div className="p-4 space-y-3">
              {forms.length === 0 ? (
                <p className="text-text-light text-center py-8">
                  No forms created yet. Create your first form above!
                </p>
              ) : (
                forms.map((form) => (
                  <div
                    key={form.id}
                    className={`p-4 rounded-xl cursor-pointer transition-all ${
                      selectedForm?.id === form.id
                        ? 'bg-primary/10 border-2 border-primary'
                        : 'bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    onClick={() => {
                      setSelectedForm(form);
                      setFormFields(form.fields.map(f => ({ ...f, required: f.required })));
                      navigate(`/forms/${form.id}`);
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold">{form.name}</h3>
                      <span className="text-xs text-text-light">
                        {form.student_count} students
                      </span>
                    </div>
                    <p className="text-xs text-text-light">
                      Created {new Date(form.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Form Details */}
        <div className="lg:col-span-2">
          {selectedForm ? (
            <div className="space-y-6">
              {/* Form Info Card */}
              <div className="card">
                <div className="flex items-center justify-between p-6 pb-4 border-b border-border">
                  <div className="flex items-center gap-3">
                    <LinkIcon className="w-6 h-6 text-primary" />
                    <h2 className="text-xl font-bold">{selectedForm.name}</h2>
                  </div>
                  <button
                    onClick={() => deleteForm(selectedForm.id)}
                    className="btn btn-outline text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="p-6">
                  {/* Form Link */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-text-light mb-2">
                      Public Signup Link
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={`${window.location.origin}/signup-form/${selectedForm.id}`}
                        readOnly
                        className="w-full px-4 py-3 border border-border rounded-xl bg-gray-50 dark:bg-gray-800/50 text-text-light"
                      />
                      <button
                        onClick={() => copyFormLink(selectedForm.id)}
                        className="btn btn-primary flex items-center gap-2"
                      >
                        {copySuccess === selectedForm.id ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <Copy className="w-5 h-5" />
                        )}
                        {copySuccess === selectedForm.id ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <p className="text-xs text-text-light mt-2">
                      Share this link on Instagram, social media, or with potential students
                    </p>
                  </div>

                  {/* Form Fields */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Form Fields</h3>
                      <button
                        onClick={() => setShowFieldEditor(!showFieldEditor)}
                        className="btn btn-outline text-sm"
                      >
                        <Edit3 className="w-4 h-4 mr-2" />
                        {showFieldEditor ? 'Close Editor' : 'Edit Fields'}
                      </button>
                    </div>

                    <div className="space-y-3">
                      {formFields.map((field) => (
                        <div
                          key={field.id}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            field.required
                              ? 'border-primary bg-primary/5'
                              : 'border-border bg-gray-50 dark:bg-gray-800/50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {field.type === 'text' && <User className="w-5 h-5 text-primary" />}
                              {field.type === 'email' && <Mail className="w-5 h-5 text-primary" />}
                              {field.type === 'tel' && <Phone className="w-5 h-5 text-primary" />}
                              {field.type === 'textarea' && <NotebookText className="w-5 h-5 text-primary" />}
                              <span className="font-semibold">{field.label}</span>
                              {field.default && (
                                <span className="px-2 py-1 rounded-full text-xs bg-gray-200 dark:bg-gray-700 text-text-light">
                                  Default
                                </span>
                              )}
                            </div>
                            
                            {showFieldEditor && !field.default && (
                              <button
                                onClick={() => toggleFieldRequirement(field.id)}
                                className={`btn btn-outline text-sm ${
                                  field.required
                                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20'
                                    : ''
                                }`}
                              >
                                {field.required ? (
                                  <CheckCircle className="w-4 h-4" />
                                ) : (
                                  <AlertCircle className="w-4 h-4" />
                                )}
                                <span className="ml-2">{field.required ? 'Required' : 'Optional'}</span>
                              </button>
                            )}
                            
                            {!showFieldEditor && (
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                field.required
                                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20'
                                  : 'bg-gray-100 text-gray-600 dark:bg-gray-800'
                              }`}>
                                {field.required ? 'Required' : 'Optional'}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Students from Form */}
              {/* <div className="card">
                <div className="flex items-center gap-3 p-6 pb-4 border-b border-border">
                  <User className="w-6 h-6 text-primary" />
                  <h2 className="text-xl font-bold">Signed Up Students</h2>
                </div>
                <div className="p-6">
                  {state.signupFormStudents?.length === 0 ? (
                    <p className="text-text-light text-center py-8">
                      No students have signed up yet. Share your form link to get started!
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {state.signupFormStudents?.map((student: any) => (
                        <div
                          key={student.id}
                          className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold">
                              {student.first_name} {student.last_name}
                            </span>
                            <span className="text-xs text-text-light">
                              {new Date(student.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="space-y-1 text-sm text-text-light">
                            <p>{student.email}</p>
                            <p>{student.phone}</p>
                            {student.note && <p className="italic">"{student.note}"</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div> */}
            </div>
          ) : (
            <div className="card p-12 text-center">
              <LinkIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2 text-text-light">
                Select a Form
              </h3>
              <p className="text-text-light">
                Choose a form from the list to view its details and manage signed-up students
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormsList;